import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import AdminTable from '../../../components/admin/AdminTable';
﻿import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { fetchPrecioPlanServicioHistory } from '../../../services/PrecioPlanServicioHistoryService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { History } from 'lucide-react';

interface HistoryEntry {
    id: number;
    user_email: string;
    user_full_name: string;
    servicio_nombre: string;
    plan_nombre: string;
    action: string;
    timestamp: string;
    changes: any;
}

const PrecioPlanServicioHistoryPage: React.FC = () => {
    const [history, setHistory] = useState<HistoryEntry[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [filterUser, setFilterUser] = useState('');
    const [filterAction, setFilterAction] = useState('');
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    const [startDate, endDate] = dateRange;
    const itemsPerPage = 10;
    const navigate = useNavigate();

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout } = useAuth();

    useEffect(() => {
        const loadHistory = async () => {
            try {
                const data = await fetchPrecioPlanServicioHistory();
                setHistory(data);
            } catch (error) {
                /* Browser diagnostics omitted. */
            }
        };
        loadHistory();
    }, []);

    const filteredHistory = history.filter(entry => {
        const matchesUser = entry.user_full_name.toLowerCase().includes(filterUser.toLowerCase());
        const matchesAction = filterAction ? entry.action === filterAction : true;
        const entryDate = new Date(entry.timestamp);
    
        // Añade este bloque idéntico al de CategoryHistoryPage
        const matchesDate = () => {
            if (!startDate && !endDate) return true;
            const start = startDate ? new Date(startDate.setHours(0, 0, 0, 0)) : null;
            const end = endDate ? new Date(endDate.setHours(23, 59, 59, 999)) : null;
    
            if (start && end) return entryDate >= start && entryDate <= end;
            if (start) return entryDate >= start;
            if (end) return entryDate <= end;
            return true;
        };
    
        return matchesUser && matchesAction && matchesDate();
    });

    const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedEntries = filteredHistory.slice(startIndex, startIndex + itemsPerPage);

    const renderChanges = (changes: any, action: string) => {
        if (!changes) return 'Sin detalles disponibles';

        const formatValue = (value: any) => {
            if (value === null || value === undefined) return 'N/A';
            if (typeof value === 'boolean') return value ? 'Activo' : 'Inactivo';
            if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
                return new Date(value).toLocaleDateString('es-BO', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
            }
            return value?.toString() || 'N/A';
        };

        if (action === 'created' || action === 'deleted') {
            return (
                <div className="space-y-1">
                    {Object.entries(changes).map(([key, value]) => (
                        <div key={key} className="flex gap-1">
                            <span className="font-medium capitalize">{key.replace('_', ' ')}:</span>
                            <span className="text-gray-600">
                                {formatValue(value)}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }

        if (action === 'updated') {
            return (
                <div className="space-y-1">
                    {Object.entries(changes).map(([key, val]) => {
                        const value = val as { old: any; new: any };
                        return (
                            <div key={key} className="flex flex-col">
                                <span className="font-medium capitalize">{key.replace('_', ' ')}</span>
                                <div className="flex gap-2 text-sm">
                                    <span className="text-red-600 line-through">{formatValue(value.old)}</span>
                                    <span>→</span>
                                    <span className="text-green-600">{formatValue(value.new)}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }

        return 'Sin detalles disponibles';
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [filterUser, filterAction, startDate, endDate]);

    return <><LumaResourceWorkspace title="Historial de precios" description="Consulta y gestiona la información de tu coworking." group="Historial" rows={filteredHistory} columns={[{key:'plan_nombre',label:'Registro'},{key:'timestamp',label:'Fecha',render:r=>new Date(r.timestamp).toLocaleString('es-BO')},{key:'user_full_name',label:'Usuario'},{key:'action',label:'Acción'},{key:'changes',label:'Detalle',render:r=>renderChanges(r.changes,r.action)}]} idKey="id" nameKey="plan_nombre" statusKey="estado" canRead={true} onCreate={undefined} onEdit={undefined} onDelete={undefined}><div className="luma-resource-panel luma-history-filters"><label>Usuario<input value={filterUser} onChange={e=>setFilterUser(e.target.value)} placeholder="Nombre del usuario"/></label><label>Acción<select value={filterAction} onChange={e=>setFilterAction(e.target.value)}><option value="">Todas las acciones</option>{Array.from(new Set(history.map(h=>h.action))).map(action=><option key={action} value={action}>{action}</option>)}</select></label><label>Desde<input type="date" value={dateRange[0]?new Date(dateRange[0]!.getTime()-dateRange[0]!.getTimezoneOffset()*60000).toISOString().slice(0,10):''} onChange={e=>setDateRange([e.target.value?new Date(e.target.value+'T00:00:00'):null,dateRange[1]])}/></label><label>Hasta<input type="date" value={dateRange[1]?new Date(dateRange[1]!.getTime()-dateRange[1]!.getTimezoneOffset()*60000).toISOString().slice(0,10):''} onChange={e=>setDateRange([dateRange[0],e.target.value?new Date(e.target.value+'T23:59:59.999'):null])}/></label></div></LumaResourceWorkspace><ExpiredSessionModal
                isOpen={isSessionExpired}
                onClose={async () => {
                    setIsSessionExpired(false);
                    await logout();
                }}
            /></>;
};

export default PrecioPlanServicioHistoryPage;

